import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ProcedureOptions } from '@trpc/server';
import { cloneDeep, isEqual } from 'lodash';
import { useParams } from 'react-router';
import { z } from 'zod';
import { create, UseBoundStore, StoreApi } from 'zustand';
import type { EditAppLoadRequest, EditAppLoadResponse, EditAppSaveRequest, EditAppSaveResponse } from 'common/ApiPayloadsDocument';
import { EditAppValidatorFunction } from 'common/ValidatorDocument';
import { usePrompt } from 'client/lib/Blocker';
import Api from './Api';
import { useError } from './Error';

type LoadRouteFunction = (
  input: {
    id: number;
    [key: string]: any;
  },
  opts?: ProcedureOptions | undefined
) => Promise<{
  status: string;
  document?: any;
}>;

type SaveRouteFunction = (
  input: {
    id: number;
    [key: string]: any;
  },
  opts?: ProcedureOptions | undefined
) => Promise<{
  status: string;
  id?: number;
  details?: string;
}>;

export interface EditAppDocument {
  id: number;
}

export interface EditAppState<D extends EditAppDocument> {
  document: D | null;
}

export type EditAppStore<S extends EditAppState<any>> = StoreApi<S>;

interface ValidationResult<D extends EditAppDocument> {
  isValid: boolean;
  isValidating: boolean;
  zodError: z.ZodError<D>;
  errorMessagesByPath: { [path: string]: string[] };
}

interface SaveError {
  status: string | null;
  details: any;
}

interface EditAppProps<D extends EditAppDocument, S extends EditAppState<D> = EditAppState<D>> {
  idKey?: string;
  loadUrl?: string | { query: LoadRouteFunction };
  saveUrl: string | { mutate: SaveRouteFunction };
  store: UseBoundStore<EditAppStore<S>>;
  validator: EditAppValidatorFunction<D>;
  saveDelayInMs?: number;
  getLoadParams?: (state: S) => any;
  getSaveParams?: (state: S) => any;
  /** Override the default check */
  isDocumentSaved?: (current: D, previous?: D) => boolean;
  onSave?: (document: D, id: number, response: EditAppSaveResponse) => void;
  onBeforeSave?: (currentDocument: D, originalDocument: D) => boolean;
  /** Return value is "showError". If set to "false", no error is shown. */
  onError?: (status: string, errorKey: 'load' | 'save') => boolean;
}

export const useEditApp = <D extends EditAppDocument, S extends EditAppState<D> = EditAppState<D>>({
  idKey = 'id',
  loadUrl,
  saveUrl,
  store,
  validator,
  saveDelayInMs = 700,
  getLoadParams,
  getSaveParams,
  isDocumentSaved,
  onSave,
  onBeforeSave,
  onError,
}: EditAppProps<D, S>): EditAppContextProps<D> => {
  const params = useParams();

  const id = parseInt(params[idKey] || '0') || 0;

  const [originalDocument, setOriginalDocument] = useState<D>();
  const [isSaving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult<D>>({
    isValid: true,
    isValidating: false,
    zodError: new z.ZodError<D>([]),
    errorMessagesByPath: {},
  });

  // This is to prevent editApp from loading in twice when the ID changes
  const [loadedIDs, setLoadedIDs] = useState<number[]>([]);
  const isReady = loadUrl === undefined || id === originalDocument?.id;

  const isSaved = useIsDocumentSaved(store, originalDocument, isDocumentSaved);
  const onSaveRef = useRef<() => void>();
  const loadingIdRef = useRef<number | null>(null);
  const loadingUrlRef = useRef<string | object | null>(null);

  const { setError } = useError(); // NOTE! This is used for load errors

  // Prevent leaving the page if there are unsaved changes
  usePrompt('Your unsaved changes will be discarded. Are you sure you want to leave?', !isSaved, () => {
    store.setState((state) => ({ ...state, document: cloneDeep(originalDocument!) }));
  });

  // Hack to call onSave after the state has updated
  // TODO: NOT USED FOR NOW, SEE LATER
  useEffect(() => {
    if (!onSaveRef.current) return;
    onSaveRef.current();
    onSaveRef.current = undefined;
  }, [isSaved]);

  const load = useCallback(async () => {
    if (!loadUrl) return;

    loadingIdRef.current = id;
    loadingUrlRef.current = loadUrl;

    let status: string;
    let document: D;
    let otherState = {};

    if (typeof loadUrl === 'string') {
      // Old handler way
      const {
        status: loadStatus,
        document: loadDocument,
        ...loadOtherState
      }: EditAppLoadResponse = await Api.post<EditAppLoadRequest>(loadUrl, {
        id,
        ...(getLoadParams ? getLoadParams(store.getState()) : {}),
      });

      status = loadStatus;
      document = loadDocument;
      otherState = loadOtherState;
    } else {
      // New tRPC way
      // TODO: This is just a hack for now, we dont take adwantage of any of react-query features...
      const {
        status: loadStatus,
        document: loadDocument,
        ...loadOtherState
      } = await loadUrl.query({ id, ...(getLoadParams ? getLoadParams(store.getState()) : {}) });

      status = loadStatus;
      document = loadDocument;
      otherState = loadOtherState;
    }

    if (status !== 'ok') {
      console.error(status);
      if (onError) {
        const showError = onError(status, 'load');
        if (!showError) return;
      }
      return setError('Something went wrong while loading data. Please try again…', { status, errorKey: 'load' });
    }

    // TODO: Is it safe to save "otherState" like this?
    store.setState((state) => ({ ...state, ...otherState, document: document as D }));
    setOriginalDocument(document);

    setLoadedIDs([id]);
  }, [loadUrl, getLoadParams, id]);

  // Initial data load
  useEffect(() => {
    const isIdentical = loadingIdRef.current === id && loadingUrlRef.current == loadUrl;
    if (isIdentical) return;
    if (loadedIDs.includes(id)) return;

    load();
  }, [id, loadedIDs, loadUrl, load]);

  useEffect(() => {
    const unsubscribe = store.subscribe(() => validate({ setIsValidating: false }));
    return () => unsubscribe();
  }, []);

  const save = useCallback(async () => {
    setError(null, { errorKey: 'save' });
    setSaveError(null);

    const isValid = validate({ setIsValidating: true });
    if (!isValid) return;

    const document = store.getState().document;
    if (!document) return;
    if (!originalDocument) return;

    if (onBeforeSave && !onBeforeSave(document, originalDocument)) return;

    setSaving(true);

    let response;
    if (typeof saveUrl === 'string') {
      // Old handler way
      response = await Api.post<EditAppSaveRequest<D>, EditAppSaveResponse>(
        saveUrl,
        {
          document,
          ...(getSaveParams ? getSaveParams(store.getState()) : {}),
        },
        { delay: saveDelayInMs, successDelay: saveDelayInMs }
      );
    } else {
      // New tRPC way
      // TODO: This is just a hack for now, we dont take advantage of any of react-query features...
      response = await saveUrl.mutate({
        document,
        ...(getSaveParams ? getSaveParams(store.getState()) : {}),
      });
    }

    setValidationResult({ isValid: true, isValidating: false, zodError: new z.ZodError<D>([]), errorMessagesByPath: {} });

    if (onSave) {
      // OLD HACK, worked, but broke for some reason?
      //  onSaveRef.current = () => onSave(document!, response.id!, response); // Hack to call onSave after the state has updated
      // NEW EVEN DIRTIER HACK
      setTimeout(() => {
        onSave(document, response.id!, response);
      }, 0);
    }

    setSaving(false);

    if (response.status !== 'ok') {
      console.error(response);
      if (onError) {
        const showError = onError(response.status, 'save');
        if (!showError) return;
      }
      //return setError('Something went wrong while saving data. Please try again…', { status: response.status, errorKey: 'save' });
      setSaveError({ status: response.status, details: response.details || null });
      return;
    }

    setOriginalDocument(cloneDeep(document));

    // Load fresh data
    load();
  }, [saveUrl, saveDelayInMs, getSaveParams, originalDocument, onSave, onBeforeSave]);

  // Validate document
  const validate = ({ setIsValidating }: { setIsValidating: boolean }) => {
    const document = store.getState().document!;

    // Validate either using schema or custom validator function
    let zodError = new z.ZodError<D>([]);
    zodError = validator(document);

    const errorMessagesByPath = zodError.flatten().fieldErrors;
    const isValid = zodError.issues.length == 0;

    if (!isValid) console.info(zodError);

    setValidationResult((state) => ({
      isValid,
      isValidating: setIsValidating ? true : state.isValidating,
      zodError,
      errorMessagesByPath: errorMessagesByPath as { [path: string]: string[] },
    }));

    return isValid;
  };

  const getDocument = useCallback(() => store.getState().document!, [store]);

  const setDocument = useCallback(
    (newDocument: Partial<D>) => store.setState((state) => ({ ...state, document: { ...state.document!, ...newDocument } })),
    [store]
  );

  const setOriginalDocumentPartial = useCallback(
    (newDocument: Partial<D>) => setOriginalDocument({ ...originalDocument!, ...newDocument }),
    [originalDocument]
  );

  return {
    id,
    load,
    save,
    validate,
    store,
    getDocument,
    setDocument,
    setOriginalDocument: setOriginalDocumentPartial,
    originalDocument,
    validationResult,
    isReady,
    isSaving,
    isSaved,
    saveError,
  };
};

interface EditAppContextProps<D extends EditAppDocument, S extends EditAppState<D> = EditAppState<D>> {
  load: () => Promise<void>;
  save: () => Promise<void>;
  validate: ({ setIsValidating }: { setIsValidating: boolean }) => boolean;
  getDocument: () => D;
  setDocument: (newDocument: Partial<D>) => void;
  setOriginalDocument: (newDocument: Partial<D>) => void;
  store: UseBoundStore<EditAppStore<S>>;
  originalDocument: D | undefined;
  isReady: boolean;
  isSaving: boolean;
  isSaved: boolean;
  validationResult: ValidationResult<D>;
  id: number;
  saveError: SaveError | null;
}

export const EditAppContext = createContext<EditAppContextProps<any>>({
  load: async () => {},
  save: async () => {},
  validate: () => true,
  getDocument: () => ({}),
  setDocument: () => {},
  setOriginalDocument: () => {},
  store: create(() => ({ document: null })),
  originalDocument: {},
  isReady: false,
  isSaving: false,
  isSaved: true,
  validationResult: { isValid: true, isValidating: false, zodError: new z.ZodError([]), errorMessagesByPath: {} },
  id: 0,
  saveError: null,
});

const useIsDocumentSaved = <D extends EditAppDocument, S extends EditAppState<D> = EditAppState<D>>(
  store: UseBoundStore<EditAppStore<S>>,
  originalDocument?: D,
  isDocumentSaved?: (current: D, previous?: D) => boolean
) =>
  store(
    useCallback(
      (state) => {
        if (!state.document) return true;
        if (isDocumentSaved) return isDocumentSaved(state.document, originalDocument);
        return isEqual(state.document, originalDocument);
      },
      [originalDocument, isDocumentSaved]
    )
  );

export const useEditAppContext = <D extends EditAppDocument>() => useContext<EditAppContextProps<D>>(EditAppContext);

export const useDocument = <D extends EditAppDocument>() => {
  const context = useContext<EditAppContextProps<D>>(EditAppContext);
  return { ...context.store((state) => state.document), setDocument: context.setDocument };
};

export const useEditAppValidation = (path: string | null) => {
  const { validationResult } = useEditAppContext();
  if (!path || !validationResult.isValidating) return null;
  return validationResult.errorMessagesByPath[path]?.[0] || null;
};

/*export const useEditAppGroupValidation = (path: string | null) => {
  const { validationResult } = useEditAppContext();
  if (!path) return 0;
  if (!validationResult) return 0;

  let count = 0;
  for (const key of Object.keys(validationResult)) {
    if (key.includes(path)) count++;
  }

  return count;
};
*/
